import React, { useState, useEffect, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { PostLoginWrapper } from './PostLoginWrapper';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { addLocale } from 'primereact/api';

// Lazy loading del componente MapDrawing
const MapDrawing = lazy(() => import('./MapDrawing'));

export const CreateField = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [fieldName, setFieldName] = useState('');
  const [crop] = useState('Viña');
  // Aquí se guardará el geojson obtenido del mapa
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  
  // Estado para la ubicación del usuario
  const [userLocation, setUserLocation] = useState([40.4168, -3.7038]); // Madrid por defecto
  const [isLocationLoaded, setIsLocationLoaded] = useState(false);
  
  // Parámetros técnicos del campo
  const [porcentajeArena, setPorcentajeArena] = useState(50);
  const [porcentajeLimo, setPorcentajeLimo] = useState(30);
  const porcentajeArcilla = Math.max(
    0,
    100 - (porcentajeArena || 0) - (porcentajeLimo || 0)
  );
  const [profundidadRaices, setProfundidadRaices] = useState(0.65);
  const [potencialHidricoInicioEstres, setPotencialHidricoInicioEstres] = useState(2);
  const [potencialHidricoMaximoEstres, setPotencialHidricoMaximoEstres] = useState(3.2);
  const [distanciaFilas, setDistanciaFilas] = useState(2.4);
  const [distanciaPlantas, setDistanciaPlantas] = useState(1.2);
  const [coeficienteCorreccionEt0, setCoeficienteCorreccionEt0] = useState(1.032);
  const [kEscorrentia, setKEscorrentia] = useState(0.5);
  
  const navigate = useNavigate();

  // Obtener la ubicación del usuario al montar el componente
  useEffect(() => {
    if (navigator.geolocation) {
      const options = {
        enableHighAccuracy: false, // Más rápido, menos preciso
        timeout: 5000,
        maximumAge: 300000 // Cache por 5 minutos
      };
      
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setUserLocation([latitude, longitude]);
          setIsLocationLoaded(true);
        },
        (error) => {
          console.error("Error al obtener la ubicación del usuario:", error);
          setIsLocationLoaded(true); // Usar ubicación por defecto
        },
        options
      );
    } else {
      console.error("La geolocalización no es compatible con este navegador.");
      setIsLocationLoaded(true);
    }
  }, []);

  const checkFieldName = () => {
    setErrorMessage('');
    if (fieldName.trim() === '') {
      setTimeout(() => {
        setErrorMessage('El nombre del campo no puede estar vacío');
      }, 100);
      return false;
    }
    return true;
  };

  const checkCrop = () => {
    setErrorMessage('');
    if (crop.trim() === '') {
      setTimeout(() => {
        setErrorMessage('Debes seleccionar un cultivo');
      }, 100);
      return false;
    }
    return true;
  };

  const createField = async () => {
    try {
      setErrorMessage('');
      if (!selectedLocation) {
        setTimeout(() => {
          setErrorMessage('Debes dibujar un polígono en el mapa');
        }, 100);
        return;
      }

      const response = await fetch('/vitisad/post_campo', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        },
        body: JSON.stringify({
          nombre_campo: fieldName,
          cultivo: crop,
          coordenadas: selectedLocation,
          porcentaje_arena: porcentajeArena,
          porcentaje_arcilla: porcentajeArcilla,
          porcentaje_limo: porcentajeLimo,
          profundidad_raices: profundidadRaices,
          potencial_hidrico_inicio_estres: potencialHidricoInicioEstres,
          potencial_hidrico_maximo_estres: potencialHidricoMaximoEstres,
          distancia_filas: distanciaFilas,
          distancia_plantas: distanciaPlantas,
          coeficiente_correccion_et0: coeficienteCorreccionEt0,
          k_escorrentia: kEscorrentia
        })
      });
      if (!response.ok) {
        const errorData = await response.json();
        setErrorMessage(errorData.error || 'Ocurrió un error al crear el campo');
        return;
      }
      const responseData = await response.json();
      navigate('/fields');
    } catch (error) {
      setErrorMessage('Ocurrió un error al crear el campo');
    }
  };

  const nextStep = async () => {
    // Limpia error para que pueda volver a mostrarse aunque sea el mismo texto
    setErrorMessage('');

    // Paso 0: validación del nombre
    if (activeIndex === 0 && !checkFieldName()) return;

    // Paso 1: validación de porcentajes antes de pasar al mapa
    if (activeIndex === 1) {
      // No permitir ningún campo numérico vacío
      const numericFields = [
        { value: porcentajeArena, nombre: 'Porcentaje de arena' },
        { value: porcentajeLimo, nombre: 'Porcentaje de limo' },
        { value: profundidadRaices, nombre: 'Profundidad de raíces' },
        { value: potencialHidricoInicioEstres, nombre: 'Potencial hídrico inicio estrés' },
        { value: potencialHidricoMaximoEstres, nombre: 'Potencial hídrico máximo estrés' },
        { value: distanciaFilas, nombre: 'Distancia entre filas' },
        { value: distanciaPlantas, nombre: 'Distancia entre plantas' },
        { value: coeficienteCorreccionEt0, nombre: 'Coeficiente corrección ET0' },
      ];

      const campoVacio = numericFields.find(
        (f) => f.value === null || f.value === undefined || f.value === ''
      );

      if (campoVacio) {
        setTimeout(() => {
          setErrorMessage(`El campo "${campoVacio.nombre}" no puede estar vacío`);
        }, 100);
        return;
      }

      if (
        porcentajeArena != null &&
        porcentajeLimo != null &&
        porcentajeArena + porcentajeLimo > 100
      ) {
        setTimeout(() => {
          setErrorMessage('La suma de porcentaje de arena y limo no puede ser mayor que 100%');
        }, 100);
        return;
      }
    }

    // Último paso: crear campo
    if (activeIndex === 2) {
      createField();
      return;
    }

    if (activeIndex < totalSteps - 1) {
      setActiveIndex(activeIndex + 1);
    }
  };

  const prevStep = () => {
    if (activeIndex > 0) {
      setActiveIndex(activeIndex - 1);
    }
  };

  const totalSteps = 3;

  addLocale('es', {
    firstDayOfWeek: 1,
    dayNames: ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'],
    dayNamesShort: ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'],
    dayNamesMin: ['D', 'L', 'M', 'X', 'J', 'V', 'S'],
    monthNames: ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'],
    monthNamesShort: ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'],
    today: 'Hoy',
    clear: 'Limpiar'
  });

  return (
    <PostLoginWrapper
      isCreateField={true}
      stepIndex={activeIndex}
      prevStep={prevStep}
      error={errorMessage}
    >
      <div className="flex align-items-center justify-content-center w-11" style={{ height: '70vh' }}>
        {activeIndex === 0 && (
          <div className="w-full desktop-max-width" style={{ flexDirection: 'column', alignItems: 'center' }}>
            <p className="text-center text-xl">Asígnale un nombre a este campo:</p>
            <InputText className="w-full" placeholder="Nombre del campo" value={fieldName} onChange={(e) => setFieldName(e.target.value)} />
            <p className="w-11 text-sm mt-2">Por ejemplo: Campo XYZ</p>
          </div>
        )}
        {activeIndex === 1 && (
          <div className="w-full desktop-max-width mt-6" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            <p className="text-center text-xl mb-4 mt-0">Parámetros técnicos del campo:</p>
            <div className="grid" style={{ width: '100%' }}>
              <div className="col-12 md:col-6" style={{ padding: '0 13px' }}>
                <div className="field">
                  <label htmlFor="porcentaje_arena" className="block mb-2">Porcentaje de arena (%):</label>
                  <InputNumber 
                    id="porcentaje_arena"
                    value={porcentajeArena} 
                    onValueChange={(e) => setPorcentajeArena(e.value)}
                    mode="decimal" 
                    min={0}
                    minFractionDigits={0}
                    maxFractionDigits={2}
                    className="w-full"
                  />
                </div>
              </div>
              <div className="col-12 md:col-6" style={{ padding: '0 13px' }}>
                <div className="field">
                  <label htmlFor="porcentaje_arcilla" className="block mb-2">Porcentaje de arcilla (%):</label>
                  <InputNumber 
                    id="porcentaje_arcilla"
                    value={porcentajeArcilla} 
                    mode="decimal" 
                    minFractionDigits={0}
                    maxFractionDigits={2}
                    readOnly
                    disabled
                    className="w-full"
                  />
                </div>
              </div>
              <div className="col-12 md:col-6" style={{ padding: '0 13px' }}>
                <div className="field">
                  <label htmlFor="porcentaje_limo" className="block mb-2">Porcentaje de limo (%):</label>
                  <InputNumber 
                    id="porcentaje_limo"
                    value={porcentajeLimo} 
                    onValueChange={(e) => setPorcentajeLimo(e.value)}
                    mode="decimal" 
                    min={0}
                    minFractionDigits={0}
                    maxFractionDigits={2}
                    className="w-full"
                  />
                </div>
              </div>
              <div className="col-12 md:col-6" style={{ padding: '0 13px' }}>
                <div className="field">
                  <label htmlFor="profundidad_raices" className="block mb-2">Profundidad de raíces (m):</label>
                  <InputNumber 
                    id="profundidad_raices"
                    value={profundidadRaices} 
                    onValueChange={(e) => setProfundidadRaices(e.value)}
                    mode="decimal" 
                    minFractionDigits={0}
                    maxFractionDigits={3}
                    className="w-full"
                  />
                </div>
              </div>
              <div className="col-12 md:col-6 hidden" style={{ padding: '0 13px' }}>
                <div className="field">
                  <label htmlFor="potencial_hidrico_inicio" className="block mb-2">Pot. hídrico inicio estrés:</label>
                  <InputNumber 
                    id="potencial_hidrico_inicio"
                    value={potencialHidricoInicioEstres} 
                    onValueChange={(e) => setPotencialHidricoInicioEstres(e.value)}
                    mode="decimal" 
                    minFractionDigits={0}
                    maxFractionDigits={2}
                    className="w-full"
                  />
                </div>
              </div>
              <div className="col-12 md:col-6 hidden" style={{ padding: '0 13px' }}>
                <div className="field">
                  <label htmlFor="potencial_hidrico_maximo" className="block mb-2">Pot. hídrico máximo estrés:</label>
                  <InputNumber 
                    id="potencial_hidrico_maximo"
                    value={potencialHidricoMaximoEstres} 
                    onValueChange={(e) => setPotencialHidricoMaximoEstres(e.value)}
                    mode="decimal" 
                    minFractionDigits={0}
                    maxFractionDigits={2}
                    className="w-full"
                  />
                </div>
              </div>
              <div className="col-12 md:col-6" style={{ padding: '0 13px' }}>
                <div className="field">
                  <label htmlFor="distancia_filas" className="block mb-2">Distancia entre filas (m):</label>
                  <InputNumber 
                    id="distancia_filas"
                    value={distanciaFilas} 
                    onValueChange={(e) => setDistanciaFilas(e.value)}
                    mode="decimal" 
                    minFractionDigits={0}
                    maxFractionDigits={2}
                    className="w-full"
                  />
                </div>
              </div>
              <div className="col-12 md:col-6" style={{ padding: '0 13px' }}>
                <div className="field">
                  <label htmlFor="distancia_plantas" className="block mb-2">Distancia entre plantas (m):</label>
                  <InputNumber 
                    id="distancia_plantas"
                    value={distanciaPlantas} 
                    onValueChange={(e) => setDistanciaPlantas(e.value)}
                    mode="decimal" 
                    minFractionDigits={0}
                    maxFractionDigits={2}
                    className="w-full"
                  />
                </div>
              </div>
              <div className="col-12 md:col-6 hidden" style={{ padding: '0 13px' }}>
                <div className="field">
                  <label htmlFor="coeficiente_correccion" className="block mb-2">Coeficiente corrección ET0:</label>
                  <InputNumber 
                    id="coeficiente_correccion"
                    value={coeficienteCorreccionEt0} 
                    onValueChange={(e) => setCoeficienteCorreccionEt0(e.value)}
                    mode="decimal" 
                    minFractionDigits={0}
                    maxFractionDigits={4}
                    className="w-full"
                  />
                </div>
              </div>
              <div className="col-12 md:col-6 hidden" style={{ padding: '0 13px' }}>
                <div className="field">
                  <label htmlFor="k_escorrentia" className="block mb-2">Coeficiente de Escorrentía:</label>
                  <InputNumber 
                    id="k_escorrentia"
                    value={kEscorrentia} 
                    onValueChange={(e) => setKEscorrentia(e.value)}
                    mode="decimal" 
                    minFractionDigits={0}
                    maxFractionDigits={2}
                    className="w-full"
                  />
                </div>
              </div>
            </div>
          </div>
        )}
        {activeIndex === 2 && (
          <div className="w-full" style={{marginTop: '10px'}}>
            <Suspense fallback={
              <div className="flex align-items-center justify-content-center" style={{ height: '500px' }}>
                <div className="text-center">
                  <i className="pi pi-spin pi-spinner" style={{ fontSize: '2em', color: 'var(--primary-color)' }}></i>
                  <p className="mt-3">Cargando mapa...</p>
                </div>
              </div>
            }>
              <MapDrawing 
                onGeojsonCreated={setSelectedLocation} 
                userLocation={userLocation}
                isLocationLoaded={isLocationLoaded}
              />
            </Suspense>
          </div>
        )}
      </div>
      <div className="w-full fixed bottom-0 left-0 bg-white" style={{ zIndex: 1000 }}>
        <div className="flex justify-content-center mb-3 w-full" style={{ position: 'relative', zIndex: 1001 }}>
          {Array.from({ length: totalSteps }).map((_, index) => (
            <span
              key={index}
              style={{
                height: '10px',
                width: '10px',
                margin: '0 5px',
                backgroundColor: activeIndex === index ? 'var(--primary-color)' : '#ccc',
                borderRadius: '50%',
                display: 'inline-block'
              }}
            ></span>
          ))}
        </div>
        <div
          className="w-full p-3 shadow-2 flex align-items-center justify-content-center cursor-pointer"
          onClick={nextStep}
        >
          <div className="flex align-items-center gap-2 no-tap-highlight">
            <span className="font-medium" style={{ color: 'var(--primary-color)' }}>
              {activeIndex === totalSteps - 1 ? 'Crear campo' : 'Siguiente'}
            </span>
          </div>
        </div>
      </div>
    </PostLoginWrapper>
  );
};
